import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  usePendingSyncStore,
  selectAllJobs,
  selectActiveCount,
} from '@/stores/usePendingSyncStore';
import { useDockLayoutStore } from '@/stores/useDockLayoutStore';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Shell-mounted "your offline recordings are syncing" surface.
 *
 * Renders **collapsed by default**: a compact pill in the bottom corner
 * showing just the spinner + count (`1/3` while active, `done · failed`
 * counters when settled). Tap to expand into the full per-session list
 * with retry/dismiss controls. This keeps the corner usable on mobile
 * where the launcher pill + this banner used to collide for the same
 * `bottom: 16, insetInlineEnd: 16` slot.
 *
 * Also reports its measured height to `useDockLayoutStore` under the
 * `'pending-sync'` slot so `MaieLauncherPill` auto-dodges upward — the
 * same dodge mechanism the next-appointment dock already uses.
 *
 * Hides itself when there's nothing to show. Failed jobs persist in
 * the banner until dismissed so the doctor doesn't lose the failure
 * signal on a route change. Expansion state is component-local and
 * does NOT persist across reloads — most mobile sessions want the
 * compact pill out of the way.
 */
export const PendingSyncBanner: React.FC = () => {
  const { isRTL } = useTranslation();
  // useShallow keeps the snapshot stable. `selectAllJobs` returns
  // `Object.values(...).sort(...)` — a fresh array reference every call —
  // which fails React 18's useSyncExternalStore cached-snapshot check and
  // throws "Maximum update depth exceeded" (#185).
  const jobs = usePendingSyncStore(useShallow(selectAllJobs));
  const active = usePendingSyncStore(selectActiveCount);
  const dismiss = usePendingSyncStore((s) => s.dismiss);
  const setBottomHeight = useDockLayoutStore((s) => s.setBottomHeight);

  const [expanded, setExpanded] = useState(false);

  // Auto-expand on first failure so the doctor sees the error without
  // having to discover the chip. Re-collapses when failures are
  // dismissed / cleared. Doesn't fire for successful completions
  // (those are quiet info, not actionable).
  const failedCount = jobs.filter((j) => j.status === 'failed').length;
  const lastFailedCount = useRef(0);
  useEffect(() => {
    if (failedCount > lastFailedCount.current) {
      setExpanded(true);
    }
    lastFailedCount.current = failedCount;
  }, [failedCount]);

  // Measure self + report to dock layout store so the launcher pill
  // dodges. ResizeObserver covers transition between compact / expanded
  // and any per-job-row growth. Clear on unmount so a dismissed banner
  // doesn't leave a phantom offset hanging around.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) {
      setBottomHeight('pending-sync', 0);
      return;
    }
    const report = () => {
      setBottomHeight('pending-sync', el.offsetHeight || 0);
    };
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setBottomHeight('pending-sync', 0);
    };
  }, [setBottomHeight, jobs.length, expanded]);

  if (jobs.length === 0) return null;

  const total = jobs.length;
  const done = jobs.filter((j) => j.status === 'done').length;
  const failed = failedCount;

  // Summary text used in both compact + expanded header
  const summary =
    active > 0
      ? isRTL
        ? `جاري معالجة ${done + 1} من ${total} تسجيل…`
        : `Processing ${done + 1} of ${total} recordings…`
      : failed > 0
        ? isRTL
          ? `${done} اكتمل · ${failed} فشل`
          : `${done} done · ${failed} failed`
        : isRTL
          ? `اكتملت ${done} تسجيلات`
          : `${done} recording${done === 1 ? '' : 's'} processed`;

  const StatusIcon =
    active > 0 ? (
      <Loader2
        className="w-4 h-4 animate-spin flex-shrink-0"
        style={{ color: 'var(--color-primary-600)' }}
      />
    ) : failed > 0 ? (
      <AlertTriangle
        className="w-4 h-4 flex-shrink-0"
        style={{ color: 'var(--color-warning)' }}
      />
    ) : (
      <CheckCircle2
        className="w-4 h-4 flex-shrink-0"
        style={{ color: 'var(--color-success)' }}
      />
    );

  // Compact pill: just the status + counter, tap to expand. Width is
  // intrinsic (no fixed max-w-sm), so it stays narrow on mobile and
  // doesn't block the bottom corner. Header is kept identical between
  // modes for visual continuity when toggling.
  return (
    <div
      ref={rootRef}
      className="fixed z-40 rounded-xl shadow-lg overflow-hidden"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-primary)',
        bottom: 16,
        insetInlineEnd: 16,
        maxWidth: expanded ? 384 : 240,
        // safe-area inset so the chip sits clear of the home indicator
        // on iPhones / Android phones with gesture bars
        marginBottom: 'env(safe-area-inset-bottom, 0)',
      }}
      role="status"
      aria-live="polite"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-3 py-2 flex items-center gap-2 text-start"
        style={{
          backgroundColor: expanded
            ? 'var(--color-bg-secondary)'
            : 'transparent',
          borderBottom: expanded
            ? '1px solid var(--color-border-primary)'
            : 'none',
        }}
        aria-expanded={expanded}
        aria-controls="pending-sync-list"
        aria-label={
          expanded
            ? isRTL
              ? 'إخفاء التفاصيل'
              : 'Hide details'
            : isRTL
              ? 'إظهار التفاصيل'
              : 'Show details'
        }
      >
        {StatusIcon}
        <span
          className="text-xs font-semibold flex-1 truncate"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {summary}
        </span>
        {expanded ? (
          <ChevronDown
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: 'var(--color-text-tertiary)' }}
            aria-hidden
          />
        ) : (
          <ChevronUp
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: 'var(--color-text-tertiary)' }}
            aria-hidden
          />
        )}
      </button>
      {expanded && (
        <ul
          id="pending-sync-list"
          className="max-h-64 overflow-y-auto"
        >
          {jobs.map((job) => {
            const label =
              job.patientName ||
              (isRTL ? 'بدون مريض' : 'Unassigned');
            const statusLabel =
              job.status === 'queued'
                ? isRTL
                  ? 'في الانتظار'
                  : 'Waiting'
                : job.status === 'uploading'
                  ? isRTL
                    ? 'يرفع الصوت'
                    : 'Uploading audio'
                  : job.status === 'reprocessing'
                    ? isRTL
                      ? 'يولّد الملاحظة'
                      : 'Generating note'
                    : job.status === 'done'
                      ? isRTL
                        ? 'جاهزة'
                        : 'Ready'
                      : job.errorCode === 'AUDIO_NOT_ON_SERVER'
                        ? isRTL
                          ? 'الصوت غير قابل للاسترداد'
                          : "Audio can't be recovered"
                        : job.errorCode === 'SYNTH_ABANDONED'
                          ? isRTL
                            ? 'تجاوز حد المحاولات'
                            : 'Retry cap reached'
                          : isRTL
                            ? 'فشل المعالجة'
                            : 'Processing failed';
            const statusColor =
              job.status === 'done'
                ? 'var(--color-success)'
                : job.status === 'failed'
                  ? 'var(--color-warning)'
                  : 'var(--color-text-secondary)';
            return (
              <li
                key={job.sessionId}
                className="px-3 py-2 flex items-center gap-2"
                style={{
                  borderBottom: '1px solid var(--color-border-primary)',
                }}
              >
                <span className="flex-shrink-0">
                  {job.status === 'done' ? (
                    <CheckCircle2
                      className="w-3.5 h-3.5"
                      style={{ color: 'var(--color-success)' }}
                    />
                  ) : job.status === 'failed' ? (
                    <AlertTriangle
                      className="w-3.5 h-3.5"
                      style={{ color: 'var(--color-warning)' }}
                    />
                  ) : (
                    <Loader2
                      className="w-3.5 h-3.5 animate-spin"
                      style={{ color: 'var(--color-primary-600)' }}
                    />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-xs font-medium truncate"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {label}
                  </div>
                  <div
                    className="text-[10px] truncate"
                    style={{ color: statusColor }}
                    title={job.errorMessage ?? undefined}
                  >
                    {statusLabel}
                  </div>
                </div>
                {(job.status === 'done' || job.status === 'failed') && (
                  <button
                    onClick={() => dismiss(job.sessionId)}
                    className="w-6 h-6 inline-flex items-center justify-center rounded-md flex-shrink-0"
                    style={{ color: 'var(--color-text-tertiary)' }}
                    aria-label={isRTL ? 'إخفاء' : 'Dismiss'}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
